import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { ref, get, push, remove } from "firebase/database";
import { useAuth } from "../context/AuthContext";
import {
  BookOpen,
  Plus,
  Trash2,
  Phone,
  User,
  GraduationCap,
  Building2,
  ChevronDown,
  X,
  ArrowLeft,
} from "lucide-react";

const SEMESTERS = [1, 2, 3, 4, 5, 6, 7, 8];

function formatDate(isoString) {
  if (!isoString) return "";
  const date = new Date(isoString);
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function PostCard({ post, currentUser, onDelete }) {
  const isOwn = currentUser && post.postedBy === currentUser.uid;
  const waLink = post.contact
    ? `https://wa.me/91${post.contact.replace(/\D/g, "").slice(-10)}`
    : null;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 relative">
      {/* Header row */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${
                post.type === "need"
                  ? "bg-amber-100 text-amber-700"
                  : "bg-green-100 text-green-700"
              }`}
            >
              {post.type === "need" ? "Need" : "Have"}
            </span>
            <span className="text-xs text-gray-400">Sem {post.semester}</span>
          </div>
          <h3 className="font-bold text-gray-800 text-base mt-1 leading-tight">
            {post.subject}
          </h3>
        </div>
        {isOwn && (
          <button
            onClick={() => onDelete(post.id)}
            className="p-1.5 rounded-full hover:bg-red-50 transition text-gray-400 hover:text-red-500 shrink-0"
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>

      {/* Details */}
      <div className="space-y-1.5 mb-3">
        {post.branch && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <GraduationCap size={14} className="text-gray-400 shrink-0" />
            <span className="truncate">{post.branch}</span>
          </div>
        )}
        {post.college && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Building2 size={14} className="text-gray-400 shrink-0" />
            <span className="truncate">{post.college}</span>
          </div>
        )}
        {post.notes && (
          <p className="text-sm text-gray-500 italic mt-1">"{post.notes}"</p>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <User size={12} />
          <span>{post.postedByName || "Anonymous"}</span>
          <span>•</span>
          <span>{formatDate(post.createdAt)}</span>
        </div>
        {post.contact && waLink && (
          <a
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 bg-green-500 hover:bg-green-600 text-white text-xs font-medium px-3 py-1.5 rounded-full transition"
          >
            <Phone size={12} />
            WhatsApp
          </a>
        )}
      </div>
    </div>
  );
}

export default function BookExchange() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("need");
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const [form, setForm] = useState({
    subject: "",
    semester: "",
    branch: "",
    college: "",
    contact: "",
    notes: "",
  });

  // Pre-fill from profile
  useEffect(() => {
    if (userProfile) {
      setForm((prev) => ({
        ...prev,
        college: userProfile.college || prev.college,
        contact: userProfile.phone || prev.contact,
      }));
    }
  }, [userProfile]);

  // Fetch posts
  useEffect(() => {
    fetchPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchPosts() {
    setLoading(true);
    try {
      const snap = await get(ref(db, "bookExchange"));
      if (snap.exists()) {
        const data = snap.val();
        const list = Object.entries(data)
          .map(([id, post]) => ({ id, ...post }))
          .sort(
            (a, b) =>
              new Date(b.createdAt) - new Date(a.createdAt)
          );
        setPosts(list);
      } else {
        setPosts([]);
      }
    } catch (err) {
      
    } finally {
      setLoading(false);
    }
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!currentUser) return;
    if (!form.subject.trim() || !form.semester || !form.branch.trim()) return;

    setSubmitting(true);
    try {
      await push(ref(db, "bookExchange"), {
        type: activeTab,
        subject: form.subject.trim(),
        semester: Number(form.semester),
        branch: form.branch.trim(),
        college: form.college.trim(),
        contact: form.contact.trim(),
        notes: form.notes.trim(),
        postedBy: currentUser.uid,
        postedByName: userProfile?.name || currentUser.displayName || "Anonymous",
        createdAt: new Date().toISOString(),
      });

      setForm((prev) => ({
        ...prev,
        subject: "",
        semester: "",
        branch: "",
        notes: "",
        // keep college and contact pre-filled
      }));
      setShowForm(false);
      setSuccessMsg("Post added successfully!");
      setTimeout(() => setSuccessMsg(""), 3000);
      await fetchPosts();
    } catch (err) {
      
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(postId) {
    if (!window.confirm("Delete this post?")) return;
    try {
      await remove(ref(db, `bookExchange/${postId}`));
      setPosts((prev) => prev.filter((p) => p.id !== postId));
    } catch (err) {
      
    }
  }

  const filteredPosts = posts.filter((p) => p.type === activeTab);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Gradient Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 pt-6 pb-8">
        <button onClick={() => navigate(-1)} className="p-1.5 -ml-1.5 rounded-full hover:bg-white/20 mb-2"><ArrowLeft size={20} className="text-white" /></button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Book Exchange</h1>
            <p className="text-blue-100 text-sm mt-0.5">
              Find &amp; share semester books
            </p>
          </div>
          <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
            <BookOpen size={24} className="text-white" />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex mt-5 bg-white/20 rounded-xl p-1 gap-1">
          <button
            onClick={() => setActiveTab("need")}
            className={`flex-1 py-2 rounded-lg text-sm font-semibold transition ${
              activeTab === "need"
                ? "bg-white text-blue-700 shadow-sm"
                : "text-white hover:bg-white/10"
            }`}
          >
            Need a Book
          </button>
          <button
            onClick={() => setActiveTab("have")}
            className={`flex-1 py-2 rounded-lg text-sm font-semibold transition ${
              activeTab === "have"
                ? "bg-white text-blue-700 shadow-sm"
                : "text-white hover:bg-white/10"
            }`}
          >
            Have a Book
          </button>
        </div>
      </div>

      <div className="px-4 pt-4 pb-28 max-w-lg mx-auto">
        {/* Success message */}
        {successMsg && (
          <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-2.5 rounded-xl text-sm font-medium">
            {successMsg}
          </div>
        )}

        {/* Post button */}
        {!showForm && (
          <button
            onClick={() => {
              if (!currentUser) {
                alert("Please log in to post.");
                return;
              }
              setShowForm(true);
            }}
            className="w-full mb-5 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-2xl transition shadow-sm"
          >
            <Plus size={18} />
            Post —{" "}
            {activeTab === "need" ? "I Need a Book" : "I Have a Book"}
          </button>
        )}

        {/* Post Form */}
        {showForm && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-gray-800 text-base">
                {activeTab === "need" ? "Request a Book" : "Share a Book"}
              </h2>
              <button
                onClick={() => setShowForm(false)}
                className="p-1 rounded-full hover:bg-gray-100 transition text-gray-500"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Subject */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Subject / Book Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="subject"
                  value={form.subject}
                  onChange={handleChange}
                  placeholder="e.g. Engineering Mathematics II"
                  required
                  className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-300 transition"
                />
              </div>

              {/* Semester */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Semester <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    name="semester"
                    value={form.semester}
                    onChange={handleChange}
                    required
                    className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-300 transition appearance-none bg-white"
                  >
                    <option value="">Select semester</option>
                    {SEMESTERS.map((s) => (
                      <option key={s} value={s}>
                        Semester {s}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={16}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />
                </div>
              </div>

              {/* Branch */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Branch / Department <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="branch"
                  value={form.branch}
                  onChange={handleChange}
                  placeholder="e.g. Computer Engineering"
                  required
                  className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-300 transition"
                />
              </div>

              {/* College */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  College
                </label>
                <input
                  type="text"
                  name="college"
                  value={form.college}
                  onChange={handleChange}
                  placeholder="Your college name"
                  className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-300 transition"
                />
              </div>

              {/* Contact */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Contact (WhatsApp number)
                </label>
                <input
                  type="tel"
                  name="contact"
                  value={form.contact}
                  onChange={handleChange}
                  placeholder="10-digit mobile number"
                  className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-300 transition"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Notes (optional)
                </label>
                <textarea
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                  placeholder="Any additional details, edition preference, condition, etc."
                  rows={3}
                  className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-300 transition resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-xl transition text-sm"
              >
                {submitting
                  ? "Posting..."
                  : activeTab === "need"
                  ? "Post Request"
                  : "Post Book"}
              </button>
            </form>
          </div>
        )}

        {/* Posts list */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-7 h-7 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <BookOpen size={52} className="text-gray-200 mb-4" />
            <p className="text-gray-600 font-semibold text-lg mb-1">
              No posts yet
            </p>
            <p className="text-gray-400 text-sm">
              Be the first to post under{" "}
              <span className="font-medium">
                {activeTab === "need" ? '"Need a Book"' : '"Have a Book"'}
              </span>
              !
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredPosts.map((post) => (
              <PostCard
                key={post.id}
                post={post}
                currentUser={currentUser}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
